import Vue from 'vue'
import api from '../api'
import { getValueByType } from '../common/types'
import deviceGroupTypes from './detail/deviceGroupTypes'
import slabTypes from './detail/slabTypes'

import _ from 'lodash'

export default {
    state: {
        bridges: {
            bridges: [],
            currentVersionCommit: '',
            paginator: {
                data: [],
                current_page: 1,
                per_page: 0,
            },
        },
        bridgesByPage: {},
        bridgesBySerial: {},
        beaconBySerial: {},
        fetchingBridges: false,
        details: {
            deviceGroup: null,
        },
        bridgeReleaseTracks: [],
    },
    getters: {
        bridges (state) {
            return state.bridges
        },
        isFetchingBridges (state) {
            return state.fetchingBridges
        },
        getBridgeBySerial (state) {
            return serialNumber => state.bridgesBySerial[serialNumber] || {}
        },
        getBeaconBySerial (state) {
            return serialNumber => state.beaconBySerial[serialNumber] || {}
        },
        getDetailTerms (state) {
            return name => state.details[name]
        },
        getDeviceBySerial (state, getters) {
            return (serialNumber, deviceSerialNumber) => {
                const bridge = getters.getBridgeBySerial(serialNumber)

                if (!bridge.devicesBySerial) {
                    bridge.devicesBySerial = {}
                }

                return bridge.devicesBySerial[deviceSerialNumber]
            }
        },
        getUsersBySerial (state, getters) {
            return (serialNumber) => {
                const bridge = getters.getBridgeBySerial(serialNumber)

                if (!bridge.users) {
                    bridge.users = []
                }

                return bridge.users
            }
        },
        getDeviceGroupByUuid (state, getters) {
            return (serialNumber, uuid) => {
                const bridge = getters.getBridgeBySerial(serialNumber)

                return bridge.deviceGroupData.deviceGroups
                    .find(deviceGroup => deviceGroup.uuid === uuid)
            }
        },
    },
    mutations: {
        setBridges (state, bridges) {
            state.bridges = bridges
        },
        setFetchingBridges (state, fetchingBridges) {
            state.fetchingBridges = fetchingBridges
        },
        setBridgeBySerial (state, bridge) {
            Vue.set(state.bridgesBySerial, bridge.bridge.serial_number, bridge)
        },
        setBeaconBySerial (state, beacon) {
            Vue.set(state.beaconBySerial, beacon.serialNumber, beacon)
        },
        updateBridge (state, { oldBridge, newBridge }) {
            Object.assign(oldBridge, newBridge)
        },
        updateBridgeInIndex (state, bridge) {
            const index = state.bridges.bridges.findIndex(stateBridge => {
                return stateBridge.serial_number === bridge.serial_number
            })
            if (index === -1) return
            Vue.set(state.bridges.bridges, index, bridge)
        },
        deleteDeviceGroup (state, { serialNumber, uuid }) {
            const bridge = state.bridgesBySerial[serialNumber]
            const deviceGroups = bridge.deviceGroupData.deviceGroups
            const indexToDelete = deviceGroups.findIndex(deviceGroup => deviceGroup.uuid === uuid)

            Vue.delete(deviceGroups, indexToDelete)
        },
        updateExternalApiClient (state, { serialNumber, uuid, data }) {
            const bridge = state.bridgesBySerial[serialNumber]
            const externalClients = bridge.externalClients
            const indexToUpdate = externalClients.findIndex(client => client.uuid === uuid)
            const client = externalClients[indexToUpdate]

            Object.assign(client, data)
        },
        setDeviceBySerial (state, { serialNumber, deviceData }) {
            const bridge = state.bridgesBySerial[serialNumber]
            const device = deviceData.device
            const deviceGroups = deviceData.deviceGroups
            const devicesBySerial = bridge.devicesBySerial || {}

            if (!device.group) {
                device.group = { uuid: '' }
            }

            Vue.set(devicesBySerial, device.serialNumber, device)
            Vue.set(bridge, 'devicesBySerial', devicesBySerial)

            if (deviceGroups) {
                Vue.set(bridge, 'deviceGroups', deviceGroups)
            }
        },
        switchDeviceGroup (state, { device, oldGroup, newGroup }) {
            if (oldGroup) {
                _.remove(oldGroup.devices, oldDevice => oldDevice.serialNumber === device.serialNumber)
            }

            newGroup.devices.push(device)
        },
        updateDetailTerms (state, details) {
            state.details = {
                ...this.details,
                ...details,
            }
        },
        addExternalApiClient (state, { bridge, client }) {
            bridge.externalClients.push(client)
        },
        addDeviceGroup (state, { bridge, deviceGroup }) {
            bridge.deviceGroupData.deviceGroups.push(deviceGroup)
        },
        setBridgeReleaseTracks (state, tracks) {
            state.bridgeReleaseTracks = tracks
        },
    },
    actions: {
        async fetchBridges ({ dispatch, commit }, { page, search, sort, direction }) {
            commit('setFetchingBridges', true)

            const response = await api.get('bridges', {
                params: {
                    page,
                    search,
                    sort,
                    direction,
                },
            })

            const bridges = response.data

            bridges.paginator.paginate = function (page) {
                dispatch('fetchBridges', { page })
            }

            Vue.set(bridges.paginator, 'data', bridges.bridges)

            commit('setBridges', bridges)
            commit('setFetchingBridges', false)
        },
        async fetchBridgeStatus ({ commit, getters }, bridge) {
            const isNestedBridge = !!bridge.bridge
            let lastSeen = -1
            try {
                const serialNumber = isNestedBridge ? bridge.bridge.serial_number : bridge.serial_number
                const { data } = await api.get(`bridges/${serialNumber}/lastSeen`)
                lastSeen = data.lastSeen
            } catch (e) {
                // ignore
            }

            if (isNestedBridge) {
                bridge.bridge.lastSeenInCloud = lastSeen
                commit('updateBridgeInIndex', bridge.bridge)
                commit('setBridgeBySerial', bridge)
            } else {
                bridge.lastSeenInCloud = lastSeen
                commit('updateBridgeInIndex', bridge)
                commit('setBridgeBySerial', { bridge })
            }
        },
        async fetchBridge ({ commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}`)
            const bridge = response.data

            commit('setBridgeBySerial', bridge)
        },
        async updateBridge ({ commit }, { bridge }) {
            const serialNumber = bridge.serial_number
            const response = await api.patch(`bridges/${serialNumber}`, bridge)

            return response
        },
        async fetchBridgeSettings ({ getters, commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/settings`)
            const bridgeData = getters.getBridgeBySerial(serialNumber)

            Vue.set(bridgeData, 'settings', response.data)

            commit('setBridgeBySerial', bridgeData)
        },
        async fetchDeviceGroups ({ getters, commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/device-groups`)
            const bridgeData = getters.getBridgeBySerial(serialNumber)

            Vue.set(bridgeData, 'deviceGroupData', response.data)
            Vue.set(bridgeData, 'deviceGroups', response.data.deviceGroups)

            commit('setBridgeBySerial', bridgeData)
        },
        async fetchDevices ({ getters, commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/devices`)
            const devices = response.data.devices
            const bridgeData = getters.getBridgeBySerial(serialNumber)

            Vue.set(bridgeData, 'devices', devices)

            commit('setBridgeBySerial', bridgeData)
        },
        async fetchUsers ({ getters, commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/users`)
            const bridgeData = getters.getBridgeBySerial(serialNumber)

            Vue.set(bridgeData, 'users', response.data.users)

            commit('setBridgeBySerial', bridgeData)
        },
        async fetchDevice ({ getters, commit }, { serialNumber, deviceSerialNumber, device }) {
            if (device) {
                commit('setDeviceBySerial', { serialNumber, deviceData: { device } })
                return device
            }

            const existingDevice = getters.getDeviceBySerial(serialNumber, deviceSerialNumber)

            if (existingDevice) {
                return existingDevice
            }

            const response = await api.get(`bridges/${serialNumber}/devices/${deviceSerialNumber}`)
            const deviceData = response.data

            commit('setDeviceBySerial', { serialNumber, deviceData })
            return deviceData.device
        },
        async assignDevice ({ getters, commit }, { serialNumber, deviceSerialNumber, group, oldGroup }) {
            const device = getters.getDeviceBySerial(serialNumber, deviceSerialNumber)
            oldGroup = getters.getDeviceGroupByUuid(serialNumber, oldGroup)
            const newGroup = getters.getDeviceGroupByUuid(serialNumber, group)
            const response = await api.patch(`bridges/${serialNumber}/devices/${deviceSerialNumber}/assign`, { group })

            commit('switchDeviceGroup', { device, oldGroup, newGroup })
            return response
        },
        async fetchExternalApiClients ({ getters, commit }, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/external-api-clients`)
            const externalClients = response.data.clients
            const bridgeData = getters.getBridgeBySerial(serialNumber)

            Vue.set(bridgeData, 'externalClients', externalClients)

            commit('setBridgeBySerial', bridgeData)
        },
        async fetchVpnBeacon ({ commit }, { serialNumber }) {
            let beacon

            try {
                const response = await api.get(`bridges/${serialNumber}/beacon`)
                beacon = response.data
            } catch (e) {
                beacon = {
                    serialNumber,
                    offline: true,
                }
            }

            commit('setBeaconBySerial', beacon)
        },
        async syncBridge ({ getters, commit }, { serialNumber }) {
            const response = await api.patch(`bridges/${serialNumber}/sync`)
            const oldBridge = getters.getBridgeBySerial(serialNumber)
            const newBridge = response.data

            commit('updateBridge', { oldBridge, newBridge })
        },
        async restartBridge ({ commit }, { serialNumber }) {
            const response = await api.patch(`bridges/${serialNumber}/restart`)

            return response
        },
        async rebootBridge ({ commit }, { serialNumber }) {
            const response = await api.patch(`bridges/${serialNumber}/reboot`)

            return response
        },
        async deleteBridge ({ commit }, { serialNumber }) {
            return await api.delete(`bridges/${serialNumber}`)
        },
        async updateDeviceGroup ({ getters, commit }, { serialNumber, deviceGroup }) {
            const uuid = deviceGroup.uuid
            const fixedDeviceGroup = { ...deviceGroup }
            const bridge = getters.getBridgeBySerial(serialNumber)

            fixedDeviceGroup['type'] = getValueByType(deviceGroupTypes, deviceGroup.type)
            fixedDeviceGroup['slab_type'] = getValueByType(slabTypes, deviceGroup.slabType)
            fixedDeviceGroup['description'] = deviceGroup.description || ''
            let response

            if (deviceGroup.uuid) {
                response = await api.patch(`bridges/${serialNumber}/device-groups/${uuid}`, fixedDeviceGroup)
            } else {
                response = await api.post(`bridges/${serialNumber}/device-groups`, fixedDeviceGroup)
                const deviceGroup = response.data

                commit('addDeviceGroup', { bridge, deviceGroup })
            }

            return response
        },
        async deleteDeviceGroup ({ commit }, { serialNumber, uuid }) {
            const response = await api.delete(`bridges/${serialNumber}/device-groups/${uuid}`)

            commit('deleteDeviceGroup', { serialNumber, uuid })
            return response
        },
        async revokeExternalApiClient ({ commit }, { serialNumber, uuid }) {
            const response = await api.patch(`bridges/${serialNumber}/external-api-clients/${uuid}/revoke`)

            commit('updateExternalApiClient', { serialNumber, uuid, data: { revoked: true } })
            return response
        },
        async acceptExternalApiClient ({ commit }, { serialNumber, uuid }) {
            const response = await api.patch(`bridges/${serialNumber}/external-api-clients/${uuid}/accept`)

            commit('updateExternalApiClient', { serialNumber, uuid, data: { revoked: false } })
            return response
        },
        async storeExternalApiClient ({ getters, commit }, { serialNumber, client }) {
            const response = await api.post(`bridges/${serialNumber}/external-api-clients`, client)
            const newClient = response.data
            const bridge = getters.getBridgeBySerial(serialNumber)

            commit('addExternalApiClient', { bridge, client: newClient })
            return response
        },
        async storeVirtualConverter ({ getters, commit }, { serialNumber, name, deviceGroupUuid }) {
            await api.post(`bridges/${serialNumber}/virtual-converter`, {
                name: name,
                device_group_uuid: deviceGroupUuid,
            })
        },
        async fetchSmartboxToken (context, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/smartbox-token`)
            const token = response.data

            return token
        },
        async fetchRecoveryLink (context, { serialNumber }) {
            const response = await api.get(`bridges/${serialNumber}/recovery-link`)
            return response.data.recoveryLink
        },
        async pinToVersion (context, { serialNumber, version }) {
            const response = await api.patch(`bridges/${serialNumber}/pin`, { version })

            return response
        },
        updateDetailTerms ({ commit }, details) {
            commit('updateDetailTerms', details)
        },
        async fetchLatestBridgeReleaseTracks ({ commit }) {
            const response = await api.get(`releases/1/latest`)
            commit('setBridgeReleaseTracks', response.data)
        },
        async changeReleaseTrack ({ commit }, { serialNumber, releaseTrack }) {
            const response = await api.patch(`bridges/${serialNumber}/release-track`, {
                'release_track': releaseTrack,
            })

            return response
        },
        async changeRecoveryEmail (context, { serialNumber, emailAddress }) {
            return await api.post(`bridges/${serialNumber}/change-recovery-email`, { emailAddress })
        },
        syncEgroDeviceGroups ({ commit }, { serialNumber }) {
            return api.post(`bridges/${serialNumber}/device-groups/sync-egro`)
        },
        syncEgroDevices ({ commit }, { serialNumber }) {
            return api.post(`bridges/${serialNumber}/devices/sync-egro`)
        },
        async inviteUser (context, { serialNumber, invite }) {
            return await api.post(`bridges/${serialNumber}/invite-user`, invite)
        },
        reinviteUser ({ commit }, { serialNumber, uuid }) {
            return api.post(`bridges/${serialNumber}/reinvite-user`, { uuid })
        },
        async deleteUser ({ commit }, { serialNumber, uuid }) {
            return api.delete(`bridges/${serialNumber}/delete-user`, {data: { uuid }})
        },
        async reassignOwner({}, { serialNumber, emailAddress}) {
            return api.post(`bridges/${serialNumber}/reassign-owner`, { emailAddress })
        },
        async updateGatewayFirmware({}, { serialNumber, hardwareAddress }){
            return api.post(`bridges/${serialNumber}/devices/update-gateway-firmware`, { hardwareAddress })
        },
        async startGatewayScan({}, { serialNumber }){
            return api.post(`bridges/${serialNumber}/devices/start-gateway-scan`)
        },
        async reconfigureGateway({}, { serialNumber, hardwareAddress }){
            return api.allowFailure().post(`bridges/${serialNumber}/devices/reconfigure-gateway`, { hardwareAddress })
        },
    },
}
